"use client";

import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { getPartnerContextNavigation } from "@/app/modules/partners/contextNavigation";
import { partnerSectionForPathname } from "@/app/modules/partners/navigation";
import { PartnerPageHeading } from "@/app/modules/partners/components/PartnerPageHeading";
import { Flash } from "../Flash";
import { AuthScreen } from "../workspace/AuthScreen";
import { WorkspaceControlPocket } from "../workspace/WorkspaceControlPocket";
import { WorkspaceMobileMenuButton } from "../workspace/WorkspaceMobileMenuButton";
import { WorkspaceMobileNav } from "../workspace/WorkspaceMobileNav";
import { WorkspaceProfileMenu } from "../workspace/WorkspaceProfileMenu";
import { WorkspaceSidebar } from "../workspace/WorkspaceSidebar";
import { useAccountSession } from "./hooks/useAccountSession";

export function PartnersAccountShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("partners.pages.account");
  const common = useTranslations("common");
  const navigation = useTranslations("partners.navigation");
  const session = useAccountSession(router);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const contextNavigation = getPartnerContextNavigation(navigation);
  const navigationPathname = partnerSectionForPathname(pathname).href;

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!profileOpen) return;

    function closeOutside(event: MouseEvent) {
      if (event.target instanceof Node && !profileMenuRef.current?.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setProfileOpen(false);
    }

    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [profileOpen]);

  if (session.loading) {
    return (
      <main className="grid min-h-[var(--app-viewport-height)] place-items-center bg-bg" aria-busy="true">
        <div role="status" className="flex items-center gap-3 text-sm text-text-secondary">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          {common("loading")}
        </div>
      </main>
    );
  }

  if (!session.user) {
    return (
      <AuthScreen
        onAuthenticated={session.reload}
        run={session.run}
        busy={session.busy}
        error={session.error}
        initialMode="register"
        contextDescription={t("authDescription")}
      />
    );
  }

  return (
    <div className="flex h-[var(--app-viewport-height)] min-h-0 overflow-hidden bg-surface">
      <WorkspaceSidebar contextNavigation={contextNavigation} homeLinks={[]} pathname={navigationPathname} />
      <WorkspaceMobileNav
        open={mobileMenuOpen}
        bottomLinks={[]}
        navigationHidden={false}
        application={null}
        contextNavigation={contextNavigation}
        homeLinks={[]}
        pathname={navigationPathname}
        showSidebar
        onClose={() => setMobileMenuOpen(false)}
      />
      <WorkspaceControlPocket>
        <WorkspaceProfileMenu
          user={session.user}
          busy={session.busy}
          open={profileOpen}
          menuRef={profileMenuRef}
          onToggle={() => setProfileOpen((open) => !open)}
          onClose={() => setProfileOpen(false)}
          onLogout={session.logout}
          onLocaleChanged={session.reload}
        />
      </WorkspaceControlPocket>
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col rounded-[var(--app-frame-radius)] bg-[var(--app-canvas-color)]">
        <header className="flex min-h-14 shrink-0 items-center gap-2 px-2 py-2 pr-24 sm:px-6 sm:pr-24 lg:min-h-28 lg:items-start lg:gap-6 lg:px-8 lg:pb-6 lg:pr-32 lg:pt-9">
          <WorkspaceMobileMenuButton open={mobileMenuOpen} onClick={() => setMobileMenuOpen(true)} />
          <PartnerPageHeading />
        </header>
        <main ref={contentRef} className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 sm:px-6 lg:px-8 lg:pb-8">
          <Flash error={session.error} message={session.message} />
          {children}
        </main>
      </div>
    </div>
  );
}
